import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { GiftStoreRole } from '../giftOrder/giftOrder.interface';
import {
  GiftCoupon,
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponRequestDto,
  GiftCouponShareStatus,
  GiftCouponStatistics,
} from './giftCoupon.interface';

// 공유 처리 (ShareStatus: NotShared → Shared)
export const shareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/share/${id}`,
  );
  return response.data;
};

// 공유 취소 (ShareStatus: Shared → NotShared)
export const unshareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/unshare/${id}`,
  );
  return response.data;
};

// 전달 확인 (confirmedAt 기록 + memo optional)
export const confirmCoupon = async (
  id: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/confirm-share/${id}`,
    memo ? { memo } : {},
  );
  return response.data;
};

// 조회 처리 (ShareStatus: Shared → Viewed)
export const viewCoupon = async (
  id: string,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(`/${role}/gift-coupon/view/${id}`);
  return response.data;
};

// 교환 신청 (RedeemStatus: Available → Requested)
export const requestCouponRedemption = async (
  id: string,
  dto: GiftCouponRequestDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/request/${id}`,
    dto,
  );
  return response.data;
};

// 발송 처리 (RedeemStatus: Requested → Delivered)
export interface DeliverCouponDto {
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
}

export const deliverCoupon = async (
  id: string,
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    redeemStatus: GiftCouponRedeemStatus.Delivered,
    'gifticon.deliveredAt': new Date().toISOString(),
    'gifticon.priceWhole': dto.priceWhole,
    'gifticon.priceRetail': dto.priceRetail,
    'gifticon.priceRebate': dto.priceRebate,
  });
  return response.data;
};

// 발송불가 처리 (RedeemStatus: Requested → Rejected)
export const rejectCoupon = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    redeemStatus: GiftCouponRedeemStatus.Rejected,
  });
  return response.data;
};

// 수신자 전화번호 수정 (Admin)
export const updateRecipientPhone = async (
  id: string,
  phoneNumber: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'recipient.phoneNumber': phoneNumber,
  });
  return response.data;
};

// Admin에서 공유 상태를 NotShared + 교환 상태를 Available로 초기화
export const resetCouponToNotShared = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    shareStatus: GiftCouponShareStatus.NotShared,
    redeemStatus: GiftCouponRedeemStatus.Available,
  });
  return response.data;
};

// 쿠폰 통계 조회 (Admin)
export const fetchGiftCouponStatistics = async (): Promise<GiftCouponStatistics> => {
  const response = await api_.get<GiftCouponStatistics>(
    `/${LowerRole.Admin}/gift-coupon/statistics`,
  );
  return response.data;
};

// 재발행 쿠폰 생성 (Admin)
export interface CreateAdditionalCouponsDto {
  giftOrderId: string;
  count: number;
  giftItemId?: string;
}

export const createAdditionalCoupons = async (
  dto: CreateAdditionalCouponsDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/additional`,
    dto,
  );
  return response.data;
};

// 재발행 쿠폰 폐기 (Admin) - DB에서 완전 삭제
export const deleteAdditionalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/${LowerRole.Admin}/gift-coupon/additional/${id}`);
};

// 재발행 쿠폰 수신자 전화번호 저장 (Admin - 기존 patch 방식)
export const updateCouponAdditionalRecipientPhone = async (
  id: string,
  additionalRecipientPhone: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    additionalRecipientPhone,
  });
  return response.data;
};

// 확인번호 + 메모 함께 업데이트 (Admin)
export const updateCouponPhoneAndMemo = async (
  id: string,
  data: { additionalRecipientPhone?: string; memo?: string },
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, data);
  return response.data;
};

// 재발행 쿠폰 수신자 정보 변경 (Store - 전용 엔드포인트)
export const updateAdditionalRecipient = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/additional-recipient/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

// 쿠폰 폐기만 (재발행 없이) (Admin)
export interface DisposeCouponsDto {
  couponIds: string[];
  reason?: string;
}

export const disposeCoupons = async (
  dto: DisposeCouponsDto,
): Promise<GiftCoupon[]> => {
  const response = await api_.post<GiftCoupon[]>(
    `/${LowerRole.Admin}/gift-coupon/dispose`,
    dto,
  );
  return response.data;
};

// 쿠폰 폐기 + 재발급 (Admin)
export interface DisposeAndReissueCouponsDto {
  couponIds: string[];
  reason?: string;
}

export interface DisposeAndReissueResult {
  disposed: GiftCoupon[];
  reissued: GiftCoupon[];
}

export const disposeAndReissueCoupons = async (
  dto: DisposeAndReissueCouponsDto,
): Promise<DisposeAndReissueResult> => {
  const response = await api_.post<DisposeAndReissueResult>(
    `/${LowerRole.Admin}/gift-coupon/dispose-and-reissue`,
    dto,
  );
  return response.data;
};

// additional → reissued 승격 (Admin)
export const promoteToReissued = async (
  id: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/promote-to-reissued`,
  );
  return response.data;
};
